// PreferencesForm (M1 §8, REQ-NTF-004): canais, idioma, janelas de não contato; aviso de mensagens obrigatórias.
import {
  Field,
  Button,
  Input,
  NativeCheckbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
} from '@giyus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import type { DoNotContactWindow, NotificationPreferences } from '../api/contract.preview';
import { useNotificationPreferences, useSaveNotificationPreferences } from '../api/queries';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

function emptyWindow(): DoNotContactWindow {
  return {
    days: [1, 2, 3, 4, 5],
    start: '20:00',
    end: '07:00',
    timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

export function PreferencesForm() {
  const { t } = useTranslation(['notifications', 'common']);
  const { data, isLoading, isError, error, refetch } = useNotificationPreferences();
  const save = useSaveNotificationPreferences();
  const [draft, setDraft] = useState<NotificationPreferences | null>(null);
  // Rastreia de qual resposta o rascunho veio, para copiá-la exatamente uma vez quando a consulta resolve
  // (padrão React "adjusting state during render", em vez de um useEffect que dispararia um render em cascata).
  const [draftSource, setDraftSource] = useState<NotificationPreferences | null>(null);
  const [saved, setSaved] = useState(false);

  if (data && data !== draftSource) {
    setDraftSource(data);
    setDraft(data);
  }

  if (isError) {
    return (
      <ProblemAlert
        error={error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }
  if (isLoading || !draft) {
    return <Skeleton className="h-64 w-full" />;
  }

  function updateWindow(index: number, patch: Partial<DoNotContactWindow>): void {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            doNotContactWindows: prev.doNotContactWindows.map((w, i) =>
              i === index ? { ...w, ...patch } : w,
            ),
          }
        : prev,
    );
  }

  return (
    <form
      className="flex flex-col gap-6"
      data-testid="preferences-form"
      onSubmit={(e) => {
        e.preventDefault();
        setSaved(false);
        save.mutate(
          {
            channels: draft.channels,
            preferredLocale: draft.preferredLocale,
            doNotContactWindows: draft.doNotContactWindows,
          },
          {
            onSuccess: () => {
              setSaved(true);
            },
          },
        );
      }}
    >
      <section className="flex flex-col gap-3">
        <h2 className="text-body font-semibold">{t('preferences.channels.title')}</h2>
        <div className="flex items-center gap-2">
          <Switch
            id="channel-email"
            checked={draft.channels.email}
            onCheckedChange={(checked) => {
              setDraft({ ...draft, channels: { ...draft.channels, email: checked } });
            }}
          />
          <label htmlFor="channel-email">{t('preferences.channels.email')}</label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="channel-sms"
            checked={draft.channels.sms}
            disabled={!draft.smsAvailable}
            onCheckedChange={(checked) => {
              setDraft({ ...draft, channels: { ...draft.channels, sms: checked } });
            }}
          />
          <label htmlFor="channel-sms">{t('preferences.channels.sms')}</label>
          {!draft.smsAvailable && (
            <span className="text-caption text-muted-foreground">
              {t('preferences.channels.smsUnavailable')}
            </span>
          )}
        </div>
        <p className="text-caption text-muted-foreground">{t('preferences.mandatoryNotice')}</p>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-body font-semibold">{t('preferences.locale.title')}</h2>
        <Select
          value={draft.preferredLocale ?? 'default'}
          onValueChange={(next) => {
            setDraft({
              ...draft,
              preferredLocale: next === 'default' ? null : (next as 'pt-BR' | 'en-US'),
            });
          }}
        >
          <SelectTrigger className="w-56" aria-label={t('preferences.locale.title')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">{t('preferences.locale.useDefault')}</SelectItem>
            <SelectItem value="pt-BR">pt-BR</SelectItem>
            <SelectItem value="en-US">en-US</SelectItem>
          </SelectContent>
        </Select>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-body font-semibold">{t('preferences.windows.title')}</h2>
        <p className="text-body text-muted-foreground">{t('preferences.windows.subtitle')}</p>
        {draft.doNotContactWindows.length === 0 && (
          <p className="text-body text-muted-foreground">{t('preferences.windows.empty')}</p>
        )}
        <ul className="flex flex-col gap-3">
          {draft.doNotContactWindows.map((window, index) => (
            <li
              key={`window-${String(index)}`}
              className="flex flex-wrap items-end gap-2 rounded-card border p-3"
            >
              <Field
                id={`window-start-${String(index)}`}
                className="flex flex-col gap-1"
                label={<>{t('preferences.windows.start')}</>}
              >
                {(control) => (
                  <Input
                    {...control}

                    type="time"
                    value={window.start}
                    onChange={(e) => {
                      updateWindow(index, { start: e.target.value });
                    }}
                  />
                )}
              </Field>
              <Field
                id={`window-end-${String(index)}`}
                className="flex flex-col gap-1"
                label={<>{t('preferences.windows.end')}</>}
              >
                {(control) => (
                  <Input
                    {...control}

                    type="time"
                    value={window.end}
                    onChange={(e) => {
                      updateWindow(index, { end: e.target.value });
                    }}
                  />
                )}
              </Field>
              {window.end <= window.start && (
                <span className="text-caption text-muted-foreground">
                  {t('preferences.windows.crossesMidnight')}
                </span>
              )}
              <fieldset className="flex flex-wrap gap-2" aria-label={t('preferences.windows.days')}>
                {WEEKDAYS.map((day) => (
                  <label key={day} className="flex items-center gap-1 text-caption">
                    <NativeCheckbox
                      checked={window.days.includes(day)}
                      onChange={(e) => {
                        updateWindow(index, {
                          days: e.target.checked
                            ? [...window.days, day].sort()
                            : window.days.filter((d) => d !== day),
                        });
                      }}
                    />
                    {day}
                  </label>
                ))}
              </fieldset>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setDraft({
                    ...draft,
                    doNotContactWindows: draft.doNotContactWindows.filter((_, i) => i !== index),
                  });
                }}
              >
                {t('preferences.windows.remove')}
              </Button>
            </li>
          ))}
        </ul>
        <Button
          type="button"
          variant="outline"
          className="self-start min-h-(--control-h-sm)"
          onClick={() => {
            setDraft({
              ...draft,
              doNotContactWindows: [...draft.doNotContactWindows, emptyWindow()],
            });
          }}
        >
          {t('preferences.windows.add')}
        </Button>
      </section>

      {save.isError && <ProblemAlert error={save.error} />}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={save.isPending}>
          {t('preferences.save')}
        </Button>
        {saved && (
          <p
            role="status"
            className="text-body text-muted-foreground"
            data-testid="preferences-saved"
          >
            {t('preferences.saved')}
          </p>
        )}
      </div>
    </form>
  );
}
